import { NoddiAsyncError } from 'noddi-types';
import type { NoddiIconName } from 'noddi-ui-common';
import type { ReactNode } from 'react';
import { z } from 'zod';
import type { AuditUser } from '../auditInfo';

/** A file on a note. Every nsdk `NoteAttachmentRecord` satisfies it. */
export type NavioNoteAttachment = {
  id: number;
  kind: 'document' | 'image';
  url: string;
  /** When the signed `url` stops working; `useNoteAttachmentUrlRefresh` schedules a refetch ahead of it. */
  expiresAt?: string | null;
  originalFilename: string;
  contentType: string;
  byteSize: number;
};

/** A note as `NavioNotesCard` and `NavioPinnedNotes` show it. Every nsdk note record satisfies it. */
export interface NavioNote {
  id: number;
  /** Shown above the text. Leave it out when it only repeats the category ("Notes from admin"). */
  title?: string;
  content: string;
  /** Pin rank: 0 is not pinned, a higher number surfaces the note higher among the pinned ones. */
  pinned: number;
  /** Ticket the note was written from, when the note form was opened on a ticket; renders as an outline badge. */
  activeTicketId?: number | null;
  /** Only used to put a new note first in the backend's order; the cards sort by pin and date. */
  sortIndex: number;
  createdAt: string;
  createdBy?: AuditUser;
  /** Who or what the note is for, shown as an outline badge. Attach it when mapping the record. */
  category?: NavioNoteCategory;
  /**
   * Who may see the note beyond staff, shown as outline badges; leave out where the surface has no
   * visibility. Attach it when mapping the record, staff-only included.
   */
  visibleTo?: readonly NavioNoteCategory[];
  attachments: NavioNoteAttachment[];
}

export type NavioNoteCategory = {
  label: string;
  icon: NoddiIconName;
};

/** A category offered when writing a note; `value` is what the record stores. */
export type NavioNoteCategoryOption<C extends string> = NavioNoteCategory & { value: C };

/** An extra item in a note's menu, between Edit and Delete. */
export type NavioNoteAction = {
  label: string;
  icon: NoddiIconName;
  onClick: () => void;
  disabled?: boolean;
};

export const noteSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Content is required')
});

export interface NoddiNote {
  id: number;
  title: string;
  content: string;
  sortIndex: number;
  createdAt: string | Date;
  updatedAt?: string | Date | null;
  createdBy?: { id: number; firstName: string; lastName: string } | null;
  updatedBy?: { id: number; firstName: string; lastName: string } | null;
  attachments: NavioNoteAttachment[];
}

export type NoteFormValues = z.input<typeof noteSchema>;

export interface NoddiNotesManagerProps<T extends NoddiNote = NoddiNote> {
  notes: T[];
  isLoading?: boolean;
  error?: NoddiAsyncError | null;
  title?: string;
  description?: string;
  emptyStateTitle?: string;
  emptyStateMessage?: string;
  /** Creates the note JSON and returns its id so attachments can upload next. Omit where notes cannot be created. */
  onCreate?: (values: NoteFormValues & { sortIndex: number }) => Promise<{ id: number }>;
  onUpdate: (
    noteId: number,
    values: Partial<NoteFormValues> & { sortIndex?: number; attachmentsToRemove?: number[] }
  ) => Promise<void>;
  /** Deletes one note. Omit where notes cannot be deleted; the card menu loses Delete. */
  onDelete?: (noteId: number) => Promise<void>;
  /** Multipart upload of every pending file, once the note JSON exists. Omit where files are not allowed. */
  onUploadAttachments?: (noteId: number, files: File[]) => Promise<void>;
  /** Deletes one saved attachment. Omit where files are not allowed. */
  onRemoveAttachment?: (noteId: number, attachmentId: number) => Promise<void>;
  isPending?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  isSuccess?: boolean;
  successTitle?: string;
  successDescription?: string;
  resetQuery?: () => void;
  renderNoteExtras?: (note: T) => ReactNode;
  /** When true, hide all create/edit/delete/reorder controls. */
  readOnly?: boolean;
  /** Refetches the notes ahead of their attachment URLs expiring; omit where no refetch exists. */
  onAttachmentUrlsExpired?: () => void | Promise<unknown>;
}
